import React from "react";
//Material imports
import Grid from "@mui/material/Grid";
//Limitless imports
import { useTranslation } from "@limitless/common/hooks";
import { useExpertDashboard } from "@limitless/expert";
import DashboardChart, { defaultChart } from "./DashboardChart";
import DashboardGraphHeader from "./DashboardGraphHeader";
import { commonStyle } from "./DashboardRewards";
import DashboardCommonTooltip from "./DashboardCommonTooltip";
import * as ReactDOMServer from "react-dom/server";
import { useTheme } from "@mui/material/styles";

const DashboardTasks = () => {
    const classes = commonStyle();
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        categories,
        fetchMontlyTasks,
        currentMonthPloting,
        taskTrendRate,
        month,
        group,
        feesEarned,
        status,
        monthSuggestions,
        taskMonths
    } = useExpertDashboard();

    const getMaxTotal = React.useMemo(() => {
        const data = [];
        for (let i = 0; i < currentMonthPloting?.respond?.length; i++) {
            data.push(
                currentMonthPloting.respond[i].value +
                    currentMonthPloting.escalate[i].value +
                    currentMonthPloting.peerReviewed[i].value +
                    currentMonthPloting.forwardReviewed[i].value +
                    currentMonthPloting.qualityReviewed[i].value
            );
        }

        return Math.max(...data);
    }, [currentMonthPloting]);

    const series = React.useMemo(() => {
        return [
            {
                name: t("responded"),
                data: currentMonthPloting.respond
            },
            {
                name: t("forwarded"),
                data: currentMonthPloting.escalate
            },
            {
                name: t("peer reviewed"),
                data: currentMonthPloting.peerReviewed
            },
            {
                name: t("forward reviewed"),
                data: currentMonthPloting.forwardReviewed
            },
            {
                name: t("quality reviewed"),
                data: currentMonthPloting.qualityReviewed
            }
        ];
    }, [currentMonthPloting]);

    React.useEffect(() => {
        if (Object.keys(monthSuggestions).length !== 0) {
            fetchMontlyTasks(month, group);
        }
    }, [monthSuggestions]);

    const defaultYaxisValue = {
        labels: {
            formatter(val) {
                return Math.round(val);
            },
            style: {
                colors: theme.palette.text.secondary
            },
            offsetX: 0
        },
        opposite: true,
        type: "numeric",
        tickAmount: 4
    };
    const DefaultYaxis =
        getMaxTotal < 5
            ? {
                  ...defaultYaxisValue,
                  max: 4
              }
            : {
                  ...defaultYaxisValue
              };
    const options = {
        chart: {
            ...defaultChart,
            stacked: true
        },
        colors: [
            theme.palette.graph?.blue || "#183A74",
            theme.palette.graph?.pink || "#D3638F",
            theme.palette.graph?.yellow || "#FFC369",
            theme.palette.graph?.green || "#4BB59D",
            theme.palette.graph?.purple || "#8C64BE"
        ],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: `${(2 + (60 / (1 + (30*Math.exp(-categories.length /3)))))}%`
            }
        },
        yaxis: DefaultYaxis,
        tooltip: {
            shared: true,
            intersect: false,
            theme: true,
            custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
                let currentTotal = 0;
                const category = [];
                series.forEach(s => {
                    category.push(s[dataPointIndex]);
                    currentTotal += s[dataPointIndex];
                });

                return ReactDOMServer.renderToStaticMarkup(
                    <DashboardCommonTooltip
                        className="task_arrow_box"
                        date={data.date}
                        value={currentTotal}
                        theme={theme}
                        valueText={t("completed tasks")}
                        category={category}
                        categoryLabel={[t("responded"), t("forwarded"), t("peer reviewed"), t("forward reviewed"), t("quality reviewed")]}
                        colorsGrid={w.globals.colors}
                    />
                );
            }
        },
        fill: {
            type: "solid"
        }
    };

    return (
        <Grid container className={classes.container}>
            <DashboardGraphHeader
                title="Tasks Handled (Last Action Taken)"
                fetchMontlyData={fetchMontlyTasks}
                leftTitle={feesEarned}
                status={status}
                rate={Math.abs(taskTrendRate).toFixed(2)}
                monthSuggestionOverride={taskMonths}
            />

            <Grid className="chart">
                <DashboardChart
                    series={series}
                    categories={categories}
                    chartType="bar"
                    crosshairsShown={false}
                    options={options}
                />
            </Grid>
        </Grid>
    );
};

export default DashboardTasks;
